import { H5, P } from "@/components/ui/typography";
import { ContainerPrimary, ContainerSecondary } from "../ui";
import { useParams } from "react-router-dom";
import { MISSING_ID_IN_ROUTE, ROLE } from "@/utils/constant";
import { SubmitButton } from "@/components/ui/button";
import {
  useInsertIntegrationFibernodeFDT,
  useIntegrationFibernodeFDTs,
} from "@/api/generate-hpdb";
import { formatDateTime } from "@/utils/helper/formatDate";
import { convertUppercaseLabelToCapitalize } from "../utils/convertUppercaseLabelToCapitalize";
import { swalError, swalSuccess } from "@/utils/lib/swal";
import { useCurrentUser } from "@/features/auth/useCurrentUser";
import { useEffect } from "react";

export function CreateFibernodeHandler({
  isReadOnly,
  onStatusChange,
}: {
  isReadOnly: boolean;
  onStatusChange: (status: string) => void;
}) {
  const { id } = useParams();
  if (!id) {
    throw new Error(MISSING_ID_IN_ROUTE);
  }

  const { roles } = useCurrentUser();
  const canAccess =
    roles?.includes(ROLE.ADMINISTRATOR) ||
    roles?.includes(ROLE.VALIDATOR_DOCUMENT_OFFICER);

  const { data: integrationFibernodeFDTs } = useIntegrationFibernodeFDTs({
    rfsId: id,
  });
  const fibernodeFdt = integrationFibernodeFDTs?.[0];
  useEffect(() => {
    if (fibernodeFdt) {
      onStatusChange(fibernodeFdt.status?.toLowerCase());
    }
  }, [fibernodeFdt, onStatusChange]);

  const { mutate, isPending } = useInsertIntegrationFibernodeFDT();

  const handleProcess = () => {
    mutate(
      {
        id: id,
      },
      {
        onSuccess: (result) => {
          const originalData = result.data.original;
          if (originalData.status === "error") {
            swalError.fire({
              title:
                originalData?.data === "Duplicate entry Fibernode"
                  ? "Already Exists"
                  : "Sorry",
              text:
                originalData?.data ??
                "Failed upload data Fibernode & FDTID to HPM",
            });
          } else {
            swalSuccess.fire({
              text: "Successfully upload data Fibernode & FDTID to HPM",
            });
          }
        },
        onError: (error) => {
          if (error.message.includes("417")) {
            swalError.fire({
              text: "City data not found, please complete it first in the city master data",
            });
          } else {
            swalError.fire({
              text: error.message,
            });
          }
        },
      },
    );
  };

  return (
    <ContainerPrimary>
      <div className="flex-grow whitespace-nowrap">
        <H5>Create Fibernode & FDTID</H5>
        <P>
          Last Update:{" "}
          {fibernodeFdt?.updatedDate
            ? formatDateTime(fibernodeFdt?.updatedDate)
            : "-"}
        </P>
      </div>
      <ContainerSecondary>
        <P className="flex-grow text-axiata-blue dark:text-neutral-300">
          {formatLabel(fibernodeFdt?.status)}
        </P>
        <SubmitButton
          loading={isPending}
          onClick={handleProcess}
          className="ml-auto"
          disabled={
            isReadOnly ||
            !canAccess ||
            !fibernodeFdt ||
            fibernodeFdt?.status === "SUCCESS" ||
            fibernodeFdt?.status === "FDT_EXIST"
          }
        >
          Process
        </SubmitButton>
      </ContainerSecondary>
    </ContainerPrimary>
  );
}

function formatLabel(status?: string) {
  if (status === "FDT_EXIST") {
    return "FDT Exists";
  }

  return convertUppercaseLabelToCapitalize(status);
}
